/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import { useCallback, useState } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import PowerCableIcon from '@atlaskit/icon-lab/core/power-cable';
import { Anchor, Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

// Each item is ~48px tall (24px icon + padding).
// 3.5 items visible ≈ 168px
const ITEM_HEIGHT = 48;
const VISIBLE_ITEMS = 3.5;

const styles = cssMap({
	container: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
		maxHeight: `${ITEM_HEIGHT * VISIBLE_ITEMS}px`,
		overflowY: 'auto',
		overflowX: 'hidden',
		width: '100%',
		minWidth: '0px',
	},
	sourceItem: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		textDecoration: 'none',
	},
	iconWrapper: {
		flexShrink: 0,
		width: '24px',
		height: '24px',
		borderRadius: token('radius.xsmall'),
		overflow: 'hidden',
	},
	title: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		minWidth: '0px',
	},
});

export type SourceItem = {
	title: string;
	url: string;
	iconUrl?: string;
};

type SourcesListProps = {
	sources: SourceItem[];
	/** Optional function to modify/wrap each source URL before it is rendered. */
	xpcWrapUrl?: (url: string, stripOnly: boolean) => string;
};

/**
 * Renders a source icon image, hiding it if the URL fails to load
 * while still preserving the wrapper space for consistent alignment.
 */
const SourceIcon = ({ iconUrl }: { iconUrl?: string }): JSX.Element => {
	const [hasError, setHasError] = useState(false);
	const handleError = useCallback(() => setHasError(true), []);

	const showFallback = !iconUrl || hasError;

	return (
		<Flex xcss={styles.iconWrapper}>
			{showFallback ? (
				<PowerCableIcon label="" color="currentColor" />
			) : (
				<img src={iconUrl} alt="" width={24} height={24} onError={handleError} />
			)}
		</Flex>
	);
};

export const SourcesList = ({ sources, xpcWrapUrl }: SourcesListProps): JSX.Element | null => {
	if (!sources || sources.length === 0) {
		return null;
	}

	return (
		<Box xcss={styles.container} testId="sources-list">
			{sources.map((source, index) => (
				<Anchor
					key={`${source.url}-${index}`}
					href={xpcWrapUrl ? xpcWrapUrl(source.url, false) : source.url}
					target="_blank"
					xcss={styles.sourceItem}
					testId="sources-list-item"
				>
					<SourceIcon iconUrl={source.iconUrl} />
					<Box xcss={styles.title}>
						<Text size="small" maxLines={1}>
							{source.title}
						</Text>
					</Box>
				</Anchor>
			))}
		</Box>
	);
};
